/**
 * Display formatting for the panes.
 *
 * ⛔ In `en-US` with an explicit `timeZone: 'UTC'`, and the label says UTC. Not because the
 * operator's local zone would be wrong to show, but because these panes are read while someone
 * is comparing a timestamp against `kubectl` output, a log line and a colleague's screenshot —
 * all of which are UTC. A silently-localised timestamp in that context is worse than a UTC one:
 * it is off by hours with nothing on screen to say so.
 *
 * A fixed locale also makes the assertions deterministic; `toLocaleString()` with the ambient
 * locale is a test that passes on the author's machine.
 */

/**
 * Format an ISO timestamp, or return it VERBATIM when it does not parse.
 *
 * ⛔ Never "Invalid Date" and never an em dash. If the producer sends something unexpected the
 * raw value is the most useful thing on screen — it is what someone would need to paste into a
 * bug report — and a placeholder would hide a real producer defect behind a tidy-looking blank.
 *
 * The one-line text is the two {@link timestampLines} joined by `, ` (`May 3, 2026, 10:29 PM UTC`),
 * so the one-line and the two-line forms cannot drift. An input that does not parse is one line.
 */
export function formatTimestamp(iso: string): string {
  return timestampLines(iso).join(', ');
}

/** The parse guard of each formatter here: the `Date` of an ISO timestamp, or `null` when it does not parse. */
function parseTimestamp(iso: string): Date | null {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });

/** The UTC calendar date of an ISO timestamp (`May 3, 2026`), or the input verbatim when it does not parse. */
export function formatDate(iso: string): string {
  const parsed = parseTimestamp(iso);
  return parsed === null ? iso : DATE_FORMATTER.format(parsed);
}

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', { timeStyle: 'short', timeZone: 'UTC' });

/**
 * An ISO timestamp as two lines for a narrow cell: the {@link formatDate} line (`May 3, 2026`), then
 * the UTC time (`10:29 PM UTC`). {@link formatTimestamp} joins the two lines. One line, the input
 * verbatim, when it does not parse.
 */
export function timestampLines(iso: string): readonly string[] {
  const parsed = parseTimestamp(iso);
  return parsed === null ? [iso] : [DATE_FORMATTER.format(parsed), `${TIME_FORMATTER.format(parsed)} UTC`];
}

/**
 * The honest empty treatment for an absent optional string.
 *
 * ⛔ An em dash, NEVER a fabricated blurb and never "No description provided." — the first is a
 * lie and the second is four words of noise where one glyph carries the same information.
 */
export const EMPTY_TEXT = '—';

export function orEmptyDash(value: string | null): string {
  // A whitespace-only description is "empty" for display purposes; treating it as content
  // renders a card with a mysterious blank line in it.
  return value === null || value.trim() === '' ? EMPTY_TEXT : value;
}
